/**
 * Auditor's Report — registry descriptor.
 *
 * The report-agnostic page shell consumes this descriptor: `defaultConfig`
 * seeds the form, `tabs` (with `enabled` predicates) drives the preview tab
 * strip, `aiSections` (with `visible` predicates) drives the optional AI
 * polish, and `build()` is the pure deterministic content engine.
 */
import {
  COMPANY_TYPE_OPTIONS, FRAMEWORK_OPTIONS, caroApplicable, ifcApplicable, mapCompanyToType,
  deriveCaroFactsFromCompany, CARO_SECTOR_OPTIONS, CARO_OVERRIDE_OPTIONS,
} from './companyTypes';
import { OPINION_TYPE_OPTIONS, QUALIFICATION_BASIS_OPTIONS } from './opinions';
import { buildMainReport } from './mainReport';
import { buildCARO } from './caro';
import { buildIFC } from './ifc';

export const AUDITOR_REPORT_DEFAULT_CONFIG = {
  companyType: 'pvt',
  companyName: '',
  cin: '',
  fyEnded: '',
  framework: 'AS',
  firmName: '',
  frn: '',
  partnerName: '',
  membershipNo: '',
  udin: '',
  place: '',
  reportDate: '',
  opinionType: 'unmodified',
  qualificationBasis: 'misstatement',
  includeKAM: false,
  includeOtherInfo: true,
  includeEmphasis: false,
  // CARO 2020 applicability — see determineCaroApplicability in companyTypes.
  caroOverride: 'auto',
  caroSector: 'none',
  caroIsOpc: false,
  caroSmallCompany: false,
  caroHoldingOrSubsidiaryOfPublic: null,
  caroPaidUpPlusReservesInr: '',
  caroBorrowingsInr: '',
  caroTotalRevenueInr: '',
};

const auditorReport = {
  id: 'auditor-report',
  label: "Independent Auditor's Report",
  shortLabel: "Auditor's Report",
  icon: '🔍',
  subtitle: 'As per ICAI Standards on Auditing, Companies Act 2013 & Rules',

  defaultConfig: AUDITOR_REPORT_DEFAULT_CONFIG,

  // Standards legend chips shown above the preview.
  standards: [
    'SA 700 (Revised)', 'SA 705 / 706', 'SA 720 (Revised)',
    'CARO 2020', 'Section 143, Cos. Act 2013', 'Rule 11, Cos. (Audit) Rules 2014',
  ],

  // Form-option lists surfaced to the config panel.
  options: {
    companyTypes: COMPANY_TYPE_OPTIONS,
    frameworks: FRAMEWORK_OPTIONS,
    opinionTypes: OPINION_TYPE_OPTIONS,
    qualificationBases: QUALIFICATION_BASIS_OPTIONS,
    caroSectors: CARO_SECTOR_OPTIONS,
    caroOverrides: CARO_OVERRIDE_OPTIONS,
  },

  // Preview tabs — `enabled(config)` hides annexures that do not apply.
  tabs: [
    { id: 'main', label: '📄 Main Report' },
    { id: 'caro', label: '📋 Annexure A — CARO 2020', enabled: (cfg) => caroApplicable(cfg) },
    { id: 'ifc', label: '🔒 Annexure B — IFC u/s 143(3)(i)', enabled: (cfg) => ifcApplicable(cfg) },
  ],

  // Sections an optional AI polish can draft — `visible(config)` gates each.
  aiSections: [
    { id: 'kam', label: 'Key Audit Matters', visible: (cfg) => !!cfg.includeKAM },
    { id: 'emphasis', label: 'Emphasis of Matter', visible: (cfg) => !!cfg.includeEmphasis },
    { id: 'disputedDues', label: 'Disputed Statutory Dues (CARO)', visible: (cfg) => caroApplicable(cfg) },
    { id: 'basisModified', label: 'Basis for Modified Opinion', visible: (cfg) => !!cfg.opinionType && cfg.opinionType !== 'unmodified' },
  ],

  /** Pure, deterministic — returns { main, caro, ifc } HTML strings. */
  build(config = {}, companyContext = {}, ai = {}) {
    return {
      main: buildMainReport(config, companyContext, ai),
      caro: buildCARO(config, companyContext, ai),
      ifc: buildIFC(config, companyContext),
    };
  },

  /** Seed config fields from the active company (user edits always win). */
  deriveConfigFromCompany(companyContext = {}) {
    return {
      companyName: companyContext.name || '',
      cin: companyContext.cin || '',
      companyType: mapCompanyToType(companyContext),
      ...deriveCaroFactsFromCompany(companyContext),
    };
  },
};

export default auditorReport;
